import { Dayjs } from 'dayjs';
import { HatDbDataContextValue } from './context/HatDbDataContext';
import { HatMetricActionAttributionData } from './partials/dashboard/HatDbSectionActionAttribution';
import { HatMetricChannelAttributionData } from './partials/dashboard/HatDbSectionChannelAttribution';

// High number has greater priority when emitting queries.
export const PRIORTY_PREVIOUS = 0;
export const PRIORTY_CURRENT = 10;

// ---------------------------------------------------------------------------------------------------------------------
// Query-based metrics.

export type HatMetric = {
    metricName: string;
    select?: (data: unknown) => unknown;
};

type HatQueryRange = {
    rangeStart?: Dayjs;
    rangeEnd?: Dayjs;
    rangePeriod?: string;
};

export type HatQueryCriteria = {
    apiToken: string;
    mediaFilter?: string | null;
    domainFilter?: string | null;
    advertiserId?: number | null;
    browserTimezone: string;
} & HatQueryRange;

type HatMetricQuery = HatMetric & HatQueryCriteria;

export type HatMetricQueryWithPriority = {
    priority: number;
} & HatMetricQuery;

const channelAttribution: HatMetric = {
    metricName: 'CHANNEL_ATTRIBUTION',
};

const pageViewsByCategory: HatMetric = {
    metricName: 'PAGE_VIEWS_BY_CATEGORY',
};

const pageViewsByContent: HatMetric = {
    metricName: 'PAGE_VIEWS_BY_CONTENT',
};

const pageViewsSpecial: HatMetric = {
    metricName: 'PAGE_VIEWS_SPECIAL',
};

const clicksByCategory: HatMetric = {
    metricName: 'CLICKS_BY_CATEGORY',
};

const formSubmissions: HatMetric = {
    metricName: 'FORM_SUBMISSIONS',
};

const tracksByEventName: HatMetric = {
    metricName: 'TRACKS_BY_EVENT_NAME',
};

const trendVisits: HatMetric = {
    metricName: 'TREND_OF_TOTAL_VISITS',
};

const trendImpressions: HatMetric = {
    metricName: 'TREND_OF_TOTAL_IMPRESSIONS',
};

// ---------------------------------------------------------------------------------------------------------------------
// Computed metrics

// Definition for a computed metric.
export type HatComputedMetric = {
    compute: (criteria: HatComputedMetricCriteria, dataContext: HatDbDataContextValue) => any;
};

// Criteria for a computed metric.
export type HatComputedMetricCriteria = {
    interval: HatInterval;
};

// Full object needed to evaluate a computed metric.
export type HatComputedMetricQuery = HatComputedMetric & HatComputedMetricCriteria;

// Possible intervals for data placed in the data context.
export type HatInterval = 'current' | 'previous';

// Derived from the sum of all visits in the channel attribution.
export const totalVisits: HatComputedMetric = {
    compute: (criteria, dataContext) => {
        const data = dataContext.getData<HatMetricChannelAttributionData>('channelAttribution', criteria.interval);
        return data?.total?.count;
    },
};

// Derived from the sum of all visits in the channel attribution.
export const totalAdInfluencedVisits: HatComputedMetric = {
    compute: (criteria, dataContext) => {
        const data = dataContext.getData<HatMetricChannelAttributionData>('channelAttribution', criteria.interval);
        return data?.total?.adInfluencedCount;
    },
};

// Derived from the sum of all actions from the actions attribution.
export const totalActions: HatComputedMetric = {
    compute: (criteria, dataContext) => {
        const data = dataContext.getData<HatMetricActionAttributionData>('actionAttribution', criteria.interval);
        return data?.total?.count;
    },
};

// Derived from the sum of all actions from the actions attribution.
export const totalAdInfluencedActions: HatComputedMetric = {
    compute: (criteria, dataContext) => {
        const data = dataContext.getData<HatMetricActionAttributionData>('actionAttribution', criteria.interval);
        return data?.total?.adInfluencedCount;
    },
};

// Collection of all metrics.
export const hatMetrics = {
    channelAttribution,
    pageViewsByCategory,
    pageViewsByContent,
    pageViewsSpecial,
    clicksByCategory,
    formSubmissions,
    tracksByEventName,

    trendVisits,
    trendImpressions,

    // Computed metrics.
    totalVisits,
    totalAdInfluencedVisits,
    totalActions,
    totalAdInfluencedActions,
};

// ---------------------------------------------------------------------------------------------------------------------
// Filters

export enum HatMediaType {
    allMedia = 'all-media',
    display = 'display',
    onlineVideo = 'online-video',
    streamingTv = 'streaming-tv',
    audio = 'audio',
    video = 'video',
}

export const mediaFilters: {
    readonly [key in HatMediaType]: {
        value?: string; // SOURCE_CATEGORY_2 value in the database.
        label: string; // Displayed in the frontend.
        tooltip?: string;
    };
} = {
    [HatMediaType.allMedia]: {
        value: '',
        label: 'All Media',
    },
    [HatMediaType.display]: {
        value: 'Display',
        label: 'Display',
    },
    [HatMediaType.onlineVideo]: {
        value: 'OLV',
        label: 'Online Video',
    },
    [HatMediaType.streamingTv]: {
        value: 'STV',
        label: 'Streaming TV',
    },
    [HatMediaType.audio]: {
        value: 'Audio',
        label: 'Audio',
    },
    [HatMediaType.video]: {
        value: 'Video',
        label: 'Video',
        tooltip:
            'Video is a legacy media type that may contain a combination of one or many Online Video and/or Streaming TV campaigns.',
    },
} as const;

/**
 * Reverse lookup a string to get the enum value.
 * @param value The value to lookup.
 * @returns The enum value or undefined if the value is not valid for this enum.
 */
export function tryGetHatMediaType(value: string) {
    const match = Object.entries(HatMediaType).find(([_, val]) => val === value);
    return match ? (match[1] as HatMediaType) : undefined;
}

/**
 * Get the actualy filter value to pass to the server for a media type filter.
 * @param mediaType The media type to look up.
 * @returns The value to pass to the HAT server for this media type filter.
 */
export function getValueForMediaFilter(mediaType: HatMediaType) {
    return mediaFilters[mediaType].value ?? mediaType;
}
